import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Tells the member the moment AI minutes finish drafting, so they don't have to
 * keep checking back. The server stamps a pending ping when minutes flip to
 * `ready`; we surface it as a toast with a jump link and clear it server-side
 * so it only ever fires once.
 */
export default function MinutesReadyPing() {
  const nav = useNavigate();
  const busy = useRef(false);
  const seen = useRef(new Set());

  useEffect(() => {
    const run = async () => {
      if (busy.current || document.hidden) return;
      busy.current = true;
      try {
        const { data } = await api.get("/minutes-ready-pings");
        for (const m of data || []) {
          if (seen.current.has(m.id)) continue;
          seen.current.add(m.id);
          toast.success("Minutes are ready", {
            description: m.title || "Your session",
            duration: 12000,
            action: { label: "View", onClick: () => nav(`/meetings/${m.id}/minutes`) },
          });
          api.post(`/minutes-ready-pings/${m.id}/seen`).catch(() => {});
        }
      } catch {
        // anonymous / offline — nothing to announce
      } finally {
        busy.current = false;
      }
    };

    run();
    const timer = setInterval(run, 30000);
    document.addEventListener("visibilitychange", run);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
    };
  }, [nav]);

  return null;
}
